import { Subscription } from "rxjs"
import Native from "../native/native"
import DOM from "../core/dom"
import { Objects } from "../core/objects"
import Playback, { PlaybackUpdateData } from "../playback/playback"
import Player, { PlayerDestroyData } from "../player"
import StorageDataModelTheme from "../storage/data-model/theme/storage-data-model-theme"
import Tasks from "../tasks/tasks"
import Color from "./color"
import UI from "./ui/ui"

/**
 * The options for the theme.
 * @author Stan Hurks
 */
export interface ThemeOptions {
    /**
     * The color pallette of the player.
     * Setting this to NULL will cause the player to load the default colors
     */
    colors?: {
        /**
         * The primary color
         */
        primary?: Color

        /**
         * The secondary color, used for labels and accents.
         * Setting this to NULL will use the default value of {r: 255, g: 255, b: 255}
         */
        secondary?: Color|null
    } | null

    /**
     * The logo
     * Setting this to NULL will remove the logo alltogether.
     */
    logo?: {
        /**
         * A URL for the logo, anything that is valid within a url() function in CSS.
         * E.g.: "https://john.doe/image.png"
         */
        src: string

        /**
         * The URL to redirect the user upon pressing the logo in the player.
         * E.g.: "https://npo.nl"
         */
        url: string

        /**
         * The amount of ms of playtime to fade the logo out after.
         * When no value is given, the logo won't fade away.
         */
        fadeOutAtMs?: number | null

        /**
         * The amount of ms that the fade-out effect of the logo should take.
         */
        fadeOutDurationMs?: number
    } | null

    /**
     * Whether or not to automatically hide the controls of the player.
     */
    autoHideControls?: boolean
}

/**
 * The theme for the video player.
 * @author Stan Hurks
 */
export default class Theme {

    /**
     * The class prefix for styles added by this class
     */
    public static readonly classPrefix = 'npo-player-style-'

    /**
     * The ID of the player, undefined when native is used.
     */
    private readonly playerId?: number
    
    /**
     * The options
     */
    private options!: ThemeOptions

    /**
     * The subscription to the `Player.destroy` RxJS subject
     */
    private readonly subscriptionDestroy!: Subscription

    /**
     * The task id's associated with this instance
     */
    private readonly taskIds: number[] = []

    /**
     * Data used by the `this.setLogoURL` function, to change
     * the URL of the logo.
     */
    private readonly setLogoURLData: {
        /**
         * The subscription to the `DOM.changed` RxJS subject.
         */
        subscriptionDOMChanged: Subscription | null
    
        /**
         * The observer ID of the `MutationObserver` that looks
         * for changes in the DOM, within the container of the
         * associated player.
         */
        containerObserverId: number | null
    } = {
        subscriptionDOMChanged: null,
        containerObserverId: null
    }

    /**
     * The data related to the effect passed to the logo.
     */
    private readonly logoEffectData: {
        /**
         * The animation time in ms.
         */
        animationTimeMs: number

        /**
         * The current state of the logo transitioning.
         */
        state: 'fade-in' | 'fade-out' | null,

        /**
         * The assigned timeout function to the effect
         */
        timeout: NodeJS.Timeout | null

        /**
         * The classes that are added to the watermark element for the effect.
         */
        addedClasses: string[]
    } = {
        animationTimeMs: 500,
        state: null,
        timeout: null,
        addedClasses: []
    }

    constructor(options: ThemeOptions, playerId?: number) {
        // Initialize
        this.options = options
        this.playerId = playerId
        
        // Subscriptions
        this.subscriptionDestroy = Player.destroy.subscribe(this.onDestroy)
    
        // Tasks
        this.taskIds = [
            ...this.taskIds,

            // Fade out logo
            Tasks.initializeTask({
                name: `Theme fade-out logo (#${this.playerId})`,
                subject: Playback.update,
                shouldExecute: (data: PlaybackUpdateData) => {
                    return data.playerId === this.playerId
                        && this.options.logo !== null
                        && this.options.logo !== undefined
                        && this.options.logo.fadeOutAtMs !== undefined
                        && this.options.logo.fadeOutAtMs !== null
                        && this.options.logo.fadeOutDurationMs !== undefined
                        && data.changedData.currentTimeMs !== undefined
                        && data.changedData.currentTimeMs >= this.options.logo.fadeOutAtMs
                        && this.logoEffectData.state !== 'fade-out'
                },
                execute: (data: PlaybackUpdateData) => {
                    this.fadeOutLogo(data)
                }
            }),

            // Fade in logo
            Tasks.initializeTask({
                name: `Theme fade-in logo (#${this.playerId})`,
                subject: Playback.update,
                shouldExecute: (data: PlaybackUpdateData) => {
                    return data.playerId === this.playerId
                        && this.options.logo !== null
                        && this.options.logo !== undefined
                        && this.options.logo.fadeOutAtMs !== undefined
                        && this.options.logo.fadeOutAtMs !== null
                        && this.options.logo.fadeOutDurationMs !== undefined
                        && data.changedData.currentTimeMs !== undefined
                        && data.changedData.currentTimeMs < this.options.logo.fadeOutAtMs
                        && this.logoEffectData.state === 'fade-out'
                },
                execute: (data: PlaybackUpdateData) => {
                    this.fadeInLogo(data)
                }
            })
        ]

        // Update the theme
        this.update()
    }

    /**
     * Update the theme and optionally merge the changed options into
     * the current options.
     * 
     * @param playerId the id of the player
     * @param changedOptions the changed options to process
     */
    public update = (changedOptions?: Partial<ThemeOptions>) => {
        console.debug(`Parsing theme... (#${this.playerId})`)

        const styleElementClass = `${Theme.classPrefix}${this.playerId || 'native'}`

        // Destroy data from previous updates
        this.destroyLogoEffectData()

        // Check if the element of the container exists
        const containerElement = this.playerId
            ? document.querySelector(`.${Player.classPrefix}${this.playerId}`)
            : document.body
        if (!containerElement) {
            console.error(`Could not update theme, the container of the player is not available. (#${this.playerId})`)
            this.onDestroy(this.playerId)
            return
        }

        // Process the new style options
        if (changedOptions) {
            const changeOptions = (current: any, updated: any): any => {
                if (updated === undefined) {
                    return current
                }
                if (updated === null) {
                    return updated
                }
                if (Objects.isObject(updated)) {
                    if (!Objects.isObject(current)) {
                        current = {}
                    }
                    for (const key of Object.keys(updated)) {
                        current[key] = changeOptions(
                            current[key],
                            updated[key]
                        )
                    }
                    return current
                }
                return updated
            }
            this.options = changeOptions(this.options, changedOptions)
        }

        // Delete the existing style element
        let styleElement = document.querySelector(`.${styleElementClass}`)
        if (styleElement) {
            styleElement.remove()
        }

        // Set CSS variables
        for (const playerId of this.playerId ? ['', `-${this.playerId}`] : ['']) {
            const defaultTheme = new StorageDataModelTheme()

            document.documentElement.style.setProperty(`--npo-player${playerId}-primary-r`, String(
                this.options.colors && this.options.colors.primary ? this.options.colors.primary.r : defaultTheme.colorPrimary.r))
            document.documentElement.style.setProperty(`--npo-player${playerId}-primary-g`, String(
                this.options.colors && this.options.colors.primary ? this.options.colors.primary.g : defaultTheme.colorPrimary.g))
            document.documentElement.style.setProperty(`--npo-player${playerId}-primary-b`, String(
                this.options.colors && this.options.colors.primary ? this.options.colors.primary.b : defaultTheme.colorPrimary.b))
    
            document.documentElement.style.setProperty(`--npo-player${playerId}-secondary-r`, String(
                this.options.colors && this.options.colors.secondary ? this.options.colors.secondary.r : defaultTheme.colorSecondary.r))
            document.documentElement.style.setProperty(`--npo-player${playerId}-secondary-g`, String(
                this.options.colors && this.options.colors.secondary ? this.options.colors.secondary.g : defaultTheme.colorSecondary.g))
            document.documentElement.style.setProperty(`--npo-player${playerId}-secondary-b`, String(
                this.options.colors && this.options.colors.secondary ? this.options.colors.secondary.b : defaultTheme.colorSecondary.b))
        }
        const primary = this.playerId
            ? `--npo-player-${this.playerId}-primary-`
            : `--npo-player-primary-`
        const secondary = this.playerId
            ? `--npo-player-${this.playerId}-secondary-`
            : `--npo-player-secondary-`

        // Set URL for logo
        if (this.options.logo) {
            this.setLogoURL()
        }

        // Helpers
        const addColorProperty = (propertyName: string, cssVariablePrefix: string|null, options?: {prefix?: string, suffix?: string, opacity?: number}) => {
            if (!cssVariablePrefix) {
                return ''
            }
            const prefix = options && options.prefix ? options.prefix : ''
            const suffix = options && options.suffix ? options.suffix : ''
            const opacity = options && options.opacity ? options.opacity : 1
            return `${propertyName}: ${prefix}rgba(var(${cssVariablePrefix}r), var(${cssVariablePrefix}g), var(${cssVariablePrefix}b), ${opacity})${suffix};`
        }

        // Generate the style element
        const defaultTheme = new StorageDataModelTheme()
        const root = `${this.playerId ? `.${Player.classPrefix}${this.playerId} ` : ''}.bmpui-ui-uicontainer`
        styleElement = document.createElement('style')
        styleElement.classList.add(styleElementClass)
        styleElement.setAttribute('type', 'text/css')
        
        // Font browser support:
        // https://css-tricks.com/snippets/css/using-font-face-in-css/
        
        styleElement.innerHTML = `
            ${UI.replaceIconColors({
                inactive: this.options.colors?.secondary || defaultTheme.colorSecondary,
                active: this.options.colors?.primary || defaultTheme.colorPrimary
            })}

            ${root} .bmpui-ui-subtitle-overlay .bmpui-ui-subtitle-label:nth-child(1n-1)::after {
                content: initial
            }

            ${root} .bmpui-container-wrapper {
                ${addColorProperty('color', secondary, {suffix: '!important'})}
                font-family: 'npo-subtitles';
            }

            ${root} .bmpui-container-wrapper *, ${root} .bmpui-ui-selectbox {
                ${addColorProperty('color', secondary)}
                font-family: 'npo-subtitles';
            }

            ${root} .bmpui-ui-watermark {
                ${
                    this.options.logo
                    ? `
                        background-image: url(${this.options.logo.src});
                        background-size: contain;
                    `
                    : `display: none;`
                }
                transition: opacity ${this.logoEffectData.animationTimeMs}ms ease-in-out;
                -webkit-transition: opacity ${this.logoEffectData.animationTimeMs}ms ease-in-out;
                -moz-transition: opacity ${this.logoEffectData.animationTimeMs}ms ease-in-out;
                -ms-transition: opacity ${this.logoEffectData.animationTimeMs}ms ease-in-out;
            }

            ${root} .bmpui-ui-watermark.fade-in {
                opacity: 1;
            }

            ${root} .bmpui-ui-watermark.fade-out {
                opacity: 0;
            }

            ${root} .bmpui-ui-watermark.fade-out-complete {
                display: none;
            }

            ${this.options.autoHideControls ? `` : `
                ${root} .bmpui-controls-hidden {
                    all: unset!important;
                }
                ${root} .bmpui-ui-controlbar.bmpui-hidden {
                    visibility: unset!important;
                    opacity: unset!important;
                }
            `}

            ${root} .bmpui-ui-controlbar {
                ${addColorProperty('background', primary, {
                    prefix: 'linear-gradient(180deg, transparent, ',
                    suffix: ')',
                    opacity: 0.7
                })}
            }

            ${root} .bmpui-seekbar-playbackposition-marker {
                ${addColorProperty('border-color', primary, {suffix: '!important'})}
            }

            ${root} .bmpui-seekbar-playbackposition {
                ${addColorProperty('background-color', primary, {suffix: '!important'})}
            }

            ${root} .bmpui-seekbar-playbackposition-marker {
                ${addColorProperty('background-color', primary, {suffix: '!important'})}
            }

            ${root} .bmpui-ui-settings-panel {
                ${addColorProperty('background-color', primary, {suffix: '!important', opacity: 0.85})}
                border-radius: 5px;
            }

            ${root} .bmpui-ui-settings-panel-item {
                ${addColorProperty('border-bottom-color', secondary, {opacity: 0.3})}
            }
        `

        // Append the body and insert the style element
        if (this.playerId) {
            document.head.appendChild(styleElement)
            setTimeout(() => {
                const style = document.head.querySelector('.' + styleElement!.className)
                style!.innerHTML = `
                    @font-face {
                        font-family: "npo-subtitles";
                        src: local("npo-subtitles"), url("/NPOSubtitlingUNI.ttf")
                    }
                    ${style!.innerHTML}
                `
            })
        } else {
            document.body.prepend(styleElement)
        }

        Native.log('Theme initialized: ' + document.body.outerHTML)
    }

    /**
     * Set the URL for the logo in the player.
     */
    private setLogoURL = () => {
        console.debug(`Setting logo URL. (#${this.playerId})`)

        const containerElement = document.querySelector(`.${Player.classPrefix}${this.playerId}`)

        // The player was destroyed
        if (!containerElement) {
            console.error(`Tried setting the logo URL for the player, but it was destroyed before the theme was. (#${this.playerId})`)
            
            this.onDestroy(this.playerId)
            return
        }

        // Destroy previously set observers and subscriptions for this function
        this.destroySetLogoURLData()

        // Start observing the container of the associated player for changes
        this.setLogoURLData.containerObserverId = DOM.observe(containerElement)

        // Is MutationObserver supported by the browser?
        if (this.setLogoURLData.containerObserverId) {
            this.setLogoURLData.subscriptionDOMChanged = DOM.changed.subscribe((observerId: number) => {
                if (observerId !== this.setLogoURLData.containerObserverId) {
                    return
                }
                if (!this.options.logo) {
                    console.error(`Trying to set the URL for a logo that no longer exists in the theme options of the player. (#${this.playerId})`)
                    this.destroySetLogoURLData()
                    this.update()
                }
                else {
                    this.executeSetLogoURL()
                }
            })
            DOM.changed.next(this.setLogoURLData.containerObserverId)
        }

        // If not supported, fall back to a 5 second timeout to set the URL.
        else {
            console.debug(`Could not use MutationObserver, falling back to a 5 second timeout to set the logo URL... (#${this.playerId})`)
            setTimeout(() => {
                this.executeSetLogoURL()
            }, 5000)
        }
    }

    /**
     * The execute function for `this.setLogoURL`, used as its core functionality.
     * 
     * Checks if the element containing the logo has been initialized in the DOM. If it does,
     * it will assign the logo URL to the logo element.
     * 
     * It has to be a seperate function, because the `setLogoURL` function looks for the element
     * containing the logo. It takes some time for that element to be loaded in the dom,
     * so this function will get called multiple times in most cases.
     */
    private executeSetLogoURL = () => {
        console.debug(`Looking for logo element... (#${this.playerId})`)

        const containerElement = document.querySelector(`.${Player.classPrefix}${this.playerId} .bmpui-ui-uicontainer`)
        const watermarkElement = document.querySelector(`.${Player.classPrefix}${this.playerId} button.bmpui-ui-watermark`)
        
        // The player was destroyed, destroy observers and subscriptions
        if (!containerElement) {
            const rootElement = document.querySelector(`.${Player.classPrefix}${this.playerId}`)
            if (rootElement) {
                console.debug(`The container element for the player isn't initialized yet. (#${this.playerId})`)
            } else {
                console.error(`The root container element for the player could not be found. Destroying theme instance. (#${this.playerId})`)
                this.onDestroy(this.playerId)
            }
            return
        }

        // There is no watermark element found
        if (!watermarkElement) {
            console.debug(`Logo element not found in video player. Re-attempting upon DOM changes in the player's container. (#${this.playerId})`)
            return
        }

        // The logo was found
        else {
            console.debug(`Logo element found. (#${this.playerId})`)
        }

        // Update the logo if it has been removed
        if (!this.options.logo) {
            console.error(`No logo was set, removing logo. (#${this.playerId})`)
            this.update()
            return
        }

        // Set the URL of the logo
        else {
            (watermarkElement as any).dataset.url = this.options.logo.url
            this.destroySetLogoURLData()
            console.debug(`Logo URL set. (#${this.playerId})`)
        }
    }

    /**
     * Fades out the logo in the player.
     * 
     * @param data The data passed to the subject
     */
    private fadeOutLogo = (data: PlaybackUpdateData) => {
        console.debug(`Fading out logo... (#${this.playerId})`)

        // Destroy the data used by other effects
        this.destroyLogoEffectData()

        // Find the logo element
        const watermarkElement = document.querySelector(`.${Player.classPrefix}${this.playerId} button.bmpui-ui-watermark`)
        if (!watermarkElement) {
            console.error(`Could not find watermark element. (#${this.playerId})`)
            return
        }

        // Start fading out
        watermarkElement.classList.add('fade-out')
        this.logoEffectData.addedClasses.push('fade-out')

        // Stop fading out
        this.logoEffectData.timeout = setTimeout(() => {
            watermarkElement.classList.add('fade-out-complete')

            this.logoEffectData.addedClasses.push('fade-out-complete')
        }, this.logoEffectData.animationTimeMs)

        // Set the state
        this.logoEffectData.state = 'fade-out'
    }

    /**
     * Fades in the logo in the player.
     * 
     * @param data The data passed to the subject
     */
    private fadeInLogo = (data: PlaybackUpdateData) => {
        console.debug(`Fading in logo... (#${this.playerId})`)

        // Destroy the data used by other effects
        this.destroyLogoEffectData()

        // Find the logo element
        const watermarkElement = document.querySelector(`.${Player.classPrefix}${this.playerId} button.bmpui-ui-watermark`)
        if (!watermarkElement) {
            console.error(`Could not find watermark element. (#${this.playerId})`)
            return
        }

        // Fade out
        watermarkElement.classList.add('fade-out')
        this.logoEffectData.addedClasses.push('fade-out')

        // Start fading in
        this.logoEffectData.timeout = setTimeout(() => {
            watermarkElement.classList.add('fade-in')
            watermarkElement.classList.remove('fade-out')
            this.logoEffectData.addedClasses.push('fade-in')
            this.logoEffectData.addedClasses.splice(this.logoEffectData.addedClasses.indexOf('fade-out'), 1)
        }, this.logoEffectData.animationTimeMs)

        // Set the state
        this.logoEffectData.state = 'fade-in'
    }

    /**
     * Destroy the data associated to the `this.setLogoURL` function.
     */
    private destroySetLogoURLData = () => {
        console.debug(`Destroying the observers and subscriptions in \`setLogoURLData\`. (#${this.playerId})`)
        
        if (this.setLogoURLData.containerObserverId !== null) {
            DOM.unobserve(this.setLogoURLData.containerObserverId)
        }
        this.setLogoURLData.subscriptionDOMChanged?.unsubscribe()
    }

    /**
     * Destroy the data associated with `this.logoEffectData`.
     */
    private destroyLogoEffectData = () => {
        // Find the watermark element
        const watermarkElement = document.querySelector(`.${Player.classPrefix}${this.playerId} button.bmpui-ui-watermark`)
        if (!watermarkElement) {
            console.debug(`No watermark element found in root container. (#${this.playerId})`)
            return
        }

        // Remove the added classes
        for (const addedClass of this.logoEffectData.addedClasses) {
            if (watermarkElement.classList.contains(addedClass)) {
                watermarkElement.classList.remove(addedClass)
            }
        }
        this.logoEffectData.addedClasses = []

        // Clear the timeout
        if (this.logoEffectData.timeout) {
            clearTimeout(this.logoEffectData.timeout)
            this.logoEffectData.timeout = null
        }

        // Reset the state
        this.logoEffectData.state = null
    }

    /**
     * Destroys all DOM alterations made by the instance of this class.
     */
    private destroyDOMAlterations = () => {
        // Remove CSS variables
        const variables = [
            `--npo-player-${this.playerId}-primary-r`,
            `--npo-player-${this.playerId}-primary-g`,
            `--npo-player-${this.playerId}-primary-b`,
            `--npo-player-${this.playerId}-secondary-r`,
            `--npo-player-${this.playerId}-secondary-g`,
            `--npo-player-${this.playerId}-secondary-b`
        ]
        for (const variable of variables) {
            document.documentElement.style.removeProperty(variable)
        }

        // Destroy style element
        let styleElement = document.querySelector(`.${Theme.classPrefix}${this.playerId}`)
        if (styleElement) {
            styleElement.remove()
        }
    }

    /**
     * Executes when the `Player.destroy` RxJS subject has been called.
     * 
     * @param data The data passed to the RxJS subject.
     */
    private onDestroy = (data?: PlayerDestroyData) => {
        // Don't destroy anything when native is used.
        if (data === undefined) {
            return
        }

        // Only destroy the player when the `all` option is selected,
        // or when the id in data matches that of the current player.
        if (data !== 'all' && data !== this.playerId) {
            return
        }

        // Reset generated and altered data in the DOM
        this.destroyDOMAlterations()

        // Destroy all subscriptions and observers
        this.subscriptionDestroy.unsubscribe()
        
        // Destroy the data associated with `this.setLogoURL`.
        this.destroySetLogoURLData()

        // Destroy all tasks
        for (const taskId of this.taskIds) {
            Tasks.stopTask(taskId)
        }
    }
}